import { API } from '../../common/constants/urls';
import {
  CoverImageBlobsDTO,
  DrawSummary,
  FileUploadErrorDTO,
  MissingFieldWarningDTO,
  PayappHeaderMappingDTO,
  ProjectData,
  ProjectInfoDTO,
  ProjectInfoListResponse,
  ProjectItem,
  ProjectLineItemsAndSummary,
  ProjectLineItemSubtotalDTO,
  ProjectSummary,
  RecentlyUploadedProjectsDTO,
  LinkedReportsDTO,
  TagDTO,
  CorporationDTO,
} from './project.type';
import { del, get, post, put } from '../../common/axiosClient';
import { replaceEmptyStringsWithNull } from './project.util';
import { GridRowId } from '@mui/x-data-grid';
import { FileUploadDTO, HistoryDTO, NoteDTO } from 'common/utils/util.type';
import { PredictedLineItemWithDivision } from '../../components/glossary/glossary.type';
import {
  ProjectAnalysis,
  ProjectAnalysisDivisionDrawsDTO,
  UpdateProjectAnalysisDTO,
  UpdateProjectAnalysisDivisionDrawsDTO,
} from '../project-analysis/project-analysis.type';
import { FileUploadStatus } from '../file-upload/fileUpload.type';
import { handleError } from '../../common/utils/util.errors';

export const getAllProjectIds = async () => {
  return (await get(`${API.PROJECT}all-ids`)) as ProjectItem[];
};
export const getProjectInfo = async (id: string) => {
  return (await get(`${API.PROJECT}${id}/info`)) as ProjectInfoDTO;
};
export const searchProjectsInIndex = async (searchRequestBodyString: string) => {
  return await post(`${API.PROJECT}search-index`, { searchOptionsString: searchRequestBodyString });
};
export const getProjectSummary = async (id: string) => {
  return (await get(`${API.PROJECT}${id}/summary`)) as ProjectSummary;
};
export const getBudgetandLineItems = async (projectId: string, drawId: string) => {
  return (await get(`${API.PROJECT}${projectId}/draws/${drawId}/budgetandlineitems`)) as ProjectLineItemsAndSummary;
};

export const createDrawLineItemSubtotal = async (drawId: string, anchorDrawLineItemId: string, label: string, range?: number | null) => {
  return (await post(`${API.PROJECT}draws/${drawId}/subtotals`, {
    anchorDrawLineItemId,
    label,
    range: range ?? null,
  })) as ProjectLineItemSubtotalDTO;
};

export const updateDrawLineItemSubtotal = async (
  subtotalId: string,
  updates: { label?: string; anchorDrawLineItemId?: string; range?: number | null; resetRange?: boolean },
) => {
  return (await put(`${API.PROJECT}subtotals/${subtotalId}`, updates)) as ProjectLineItemSubtotalDTO;
};

export const deleteDrawLineItemSubtotal = async (subtotalId: string) => {
  return await del(`${API.PROJECT}subtotals/${subtotalId}`);
};
export const getAllProjectsInfo = async (queryParams: string[]) => {
  const queryString = queryParams.join('&');
  return (await get(`${API.PROJECT}all-projects?${queryString}`)) as ProjectInfoListResponse;
};

export const getAllProjectsCount = async () => {
  return (await get(`${API.PROJECT}all-projects-count`)) as number;
};

export const getTopFiveRecentlyUploadedProjects = async (userId: string | null) => {
  const queryString = userId ? `?userId=${userId}` : '';
  return (await get(`${API.PROJECT}top-five-recent/${queryString}`)) as RecentlyUploadedProjectsDTO[];
};

export const getLinkedReports = async (projectId?: string) => {
  return (await get(`${API.PROJECT}${projectId}/linked-reports`)) as LinkedReportsDTO[];
};

export const getProjectDraws = async (id: string) => {
  return (await get(`${API.PROJECT}${id}/draws`)) as DrawSummary[];
};

export const FilterProjectsWithLinkedReports = async (projectIds: readonly GridRowId[]) => {
  return await post(`${API.PROJECT}filter-projects-with-linked-reports`, projectIds);
};

export const bulkDeleteProjects = async (ids: GridRowId[]) => {
  return await del(`${API.PROJECT}bulk-delete`, {
    headers: {
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(ids),
  });
};

export const fetchProjects = async (requestBody: any) => {
  const requestBodyString = JSON.stringify(requestBody);
  const response = await searchProjectsInIndex(requestBodyString);
  return response;
};
export const updateProjectSummary = async (projectId: string, projectDto: Partial<ProjectSummary>) => {
  const updatedDto = replaceEmptyStringsWithNull(projectDto);
  return (await put(`${API.PROJECT}${projectId}/summary`, updatedDto)) as ProjectSummary;
};

export const updateProjectData = async (projectId: string, projectDto: ProjectData) => {
  const updatedSummaryDto = replaceEmptyStringsWithNull(projectDto.projectSummary);
  projectDto.projectSummary = updatedSummaryDto;
  return (await put(`${API.PROJECT}${projectId}`, projectDto)) as ProjectData;
};

export const globalUpdateProjectData = async (projectId: string, projectDto: any) => {
  if (projectDto?.projectSummary) {
    const updatedSummaryDto = replaceEmptyStringsWithNull(projectDto.projectSummary);
    projectDto.projectSummary = updatedSummaryDto;
  }
  return (await put(`${API.PROJECT}${projectId}/update`, projectDto)) as ProjectData;
};

export const updateProjectErrors = async (projectId: string, errors: FileUploadErrorDTO[]) => {
  return await put(`${API.PROJECT}${projectId}/errors`, errors);
};

export const updateHiddenFieldWarnings = async (projectId: string, hidden: MissingFieldWarningDTO[]) => {
  return await put(`${API.PROJECT}${projectId}/hidden-field-warnings`, hidden);
};

export const updateProjectIncompleteFlag = async (projectId: string) => {
  return await get(`${API.PROJECT}${projectId}/update-incomplete-flag`);
};

export const addOrUpdateNoteToProject = async (projectId: string, requestDto: NoteDTO) => {
  return await put(`${API.PROJECT}${projectId}/notes`, requestDto);
};
export const getNotes = async (projectId: string) => {
  return await get(`${API.PROJECT}${projectId}/notes`);
};
export const deleteNoteFromProject = async (projectId: string, noteId: string) => {
  return await del(`${API.PROJECT}${projectId}/notes/${noteId}`);
};

export const updateDivisionLineItems = async (lineItemDtos: PredictedLineItemWithDivision[]) => {
  return await put(`${API.LINE_ITEMS}/division-update`, lineItemDtos);
};

export const getProjectAnalysis = async (projectId: string) => {
  return (await get(`${API.PROJECT}${projectId}/analysis`)) as ProjectAnalysis;
};

export const updateProjectAnalysisMetadata = async (projectId: string, updateDTO: UpdateProjectAnalysisDTO) => {
  return await put(`${API.PROJECT}${projectId}/analysis`, updateDTO);
};

export const getProjectDivisionDraws = async (id: string) => {
  return (await get(`${API.PROJECT}${id}/division-draws`)) as ProjectAnalysisDivisionDrawsDTO;
};

export const updateProjectDivisionDraws = async (id: string, requestDto: UpdateProjectAnalysisDivisionDrawsDTO) => {
  return (await put(`${API.PROJECT}${id}/division-draws`, requestDto)) as boolean;
};
export const getProjectHistory = async (projectId: string, page: number, pageSize = 10) => {
  return (await get(`${API.PROJECT}${projectId}/history?page=${page}&pageSize=${pageSize}`)) as HistoryDTO[];
};

export const addProjectHistory = async (
  projectId: string,
  auditMessages: Array<{
    Section: string;
    DrawName?: string;
    Message: string;
    Type: string;
  }>,
) => {
  return await post(`${API.PROJECT}${projectId}/history`, auditMessages);
};

export const getFullProjectHistoryById = async (historyId: string): Promise<HistoryDTO> => {
  return (await get(`${API.PROJECT}history/${historyId}`)) as HistoryDTO;
};

export const exportProjectToExcel = async (projectId: string) => {
  return await get(`${API.PROJECT}export-project/${projectId}`, {
    responseType: 'blob',
  });
};

export const exportProjectsCsv = async (searchOptionsString?: string) => {
  return await post(
    `${API.PROJECT}export-csv`,
    { searchOptionsString: searchOptionsString ?? '' },
    {
      responseType: 'blob',
    },
  );
};

export const getProjectFiles = async (projectId: string) => {
  return (await get(`${API.PROJECT}${projectId}/files`)) as FileUploadDTO[];
};

export const getFileUploadUrl = async (blobName: string) => {
  const encodedBlobName = encodeURIComponent(blobName);
  return (await get(`${API.PROJECT}files/${encodedBlobName}/url`)) as string;
};

export const updatePayappHeaderMappings = async (projectId: string, mappings: PayappHeaderMappingDTO[]) => {
  return await put(`${API.PROJECT}${projectId}/payapp-header-mappings`, mappings);
};

export const deleteCoverImage = async (projectId: string) => {
  return await del(`${API.PROJECT}${projectId}/cover-image`);
};

export const uploadCoverImage = async (
  file: File,
  onUpdateFileItem: (fileName: string, status: keyof typeof FileUploadStatus, errors: FileUploadErrorDTO[], progress: number) => void,
  projectId: string,
) => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = (await post(`${API.PROJECT}${projectId}/upload-cover-image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent: ProgressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onUpdateFileItem(file.name, FileUploadStatus.Uploading, [], progress);
        if (progress === 100) {
          onUpdateFileItem(file.name, FileUploadStatus.Parsing, [], progress);
        }
      },
    })) as CoverImageBlobsDTO;

    const parsedData = response;
    onUpdateFileItem(file.name, FileUploadStatus.Success, [], 100);
    return parsedData;
  } catch (error: any) {
    console.error('Error uploading Cover Image:', error);
    onUpdateFileItem(
      file.name,
      FileUploadStatus.Failed,
      [
        {
          fieldName: 'Unknown',
          lineItem: '',
          message: error.message || 'Unexpected error occurred',
        },
      ],
      0,
    );
    return Promise.reject(error);
  }
};

export const retrieveProjectDraws = async (projectId: string) => {
  return await get(`${API.PROJECTS}${projectId}/draws/`);
};

export const getAllCorporations = async (): Promise<CorporationDTO[]> => {
  try {
    return await get('/project/corporations');
  } catch (error) {
    handleError(error);
    throw error;
  }
};

export const getAllTags = async (): Promise<TagDTO[]> => {
  try {
    return await get('/project/tags');
  } catch (error) {
    handleError(error);
    throw error;
  }
};

export const getAllObservers = async () => {
  try {
    return await get(`${API.USER}/observers`);
  } catch (error) {
    handleError(error);
    throw error;
  }
};

// User preferences (reusable across listing pages)
export const getUserColumnPreferences = async (pageKey: string) => {
  return await get(`${API.USER}/preferences/${pageKey}`);
};

export const saveUserColumnPreferences = async (pageKey: string, columnsJson: any) => {
  return await put(`${API.USER}/preferences/${pageKey}`, { pageKey, columnsJson: JSON.stringify(columnsJson) });
};

export const saveUserFilterPreferences = async (pageKey: string, filtersJson: any) => {
  return await put(`${API.USER}/preferences/${pageKey}`, { pageKey, filtersJson: JSON.stringify(filtersJson) });
};
